import React, { useMemo, useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import LanguageIcon from '@mui/icons-material/Language';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';

import { useColorMode } from '../colorMode';
import brandIcon from '../assets/alamen-icon.png';
import { URBANAI_URL } from '../lib/seo';

export default function Header() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isArabic = i18n.language === 'ar';
  const { mode, toggle } = useColorMode();
  const location = useLocation();

  const [sectorsAnchorEl, setSectorsAnchorEl] = useState(null);
  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileSectorsOpen, setMobileSectorsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const sectorsOpen = Boolean(sectorsAnchorEl);
  const langOpen = Boolean(langAnchorEl);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const sectors = useMemo(
    () => [
      { label: t('sectorsData.government.title'), to: '/sectors#government' },
      { label: t('sectorsData.justice.title'), to: '/sectors#justice' },
      { label: t('sectorsData.healthcare.title'), to: '/sectors#healthcare' },
      { label: t('sectorsData.finance.title'), to: '/sectors#finance' },
      { label: t('sectorsData.logistics.title'), to: '/sectors#logistics' },
      { label: t('sectorsData.energy.title'), to: '/sectors#energy' }
    ],
    [t]
  );

  const setLanguage = async (lng) => {
    await i18n.changeLanguage(lng);
    setLangAnchorEl(null);
  };

  const linkSx = {
    color: 'text.primary',
    fontWeight: 500,
    fontSize: '0.92rem',
    letterSpacing: '-0.005em',
    textTransform: 'none',
    '&:hover': { bgcolor: 'transparent', color: 'primary.main' }
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: scrolled
          ? mode === 'dark'
            ? 'rgba(10,10,10,0.78)'
            : 'rgba(250,248,242,0.78)'
          : 'background.default',
        backdropFilter: scrolled ? 'saturate(180%) blur(18px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'saturate(180%) blur(18px)' : 'none',
        borderBottom: scrolled ? '1px solid' : '1px solid transparent',
        borderColor: 'divider',
        color: 'text.primary',
        transition: 'background-color 180ms ease, border-color 180ms ease'
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ py: 0.75, minHeight: { xs: 56, md: 64 } }}>
          {/* Wordmark */}
          <Stack
            direction="row"
            spacing={1.2}
            alignItems="center"
            component={RouterLink}
            to="/"
            sx={{ textDecoration: 'none', color: 'text.primary' }}
          >
            <Box
              component="img"
              src={brandIcon}
              alt={t('brand.name')}
              sx={{ height: { xs: 30, md: 34 }, width: 'auto', display: 'block' }}
            />
            <Typography
              sx={{
                fontFamily: 'var(--font-serif)',
                fontWeight: 400,
                fontSize: '1.6rem',
                lineHeight: 1,
                letterSpacing: '-0.02em'
              }}
            >
              {t('brand.name')}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.68rem',
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                display: { xs: 'none', sm: 'inline' }
              }}
            >
              {t('brand.tagline')}
            </Typography>
          </Stack>

          <Box sx={{ flexGrow: 1 }} />

          {isMobile ? (
            <>
              <IconButton
                aria-label={mode === 'dark' ? t('common.lightMode') : t('common.darkMode')}
                onClick={toggle}
                sx={{ color: 'text.primary' }}
              >
                {mode === 'dark' ? <LightModeOutlined fontSize="small" /> : <DarkModeOutlined fontSize="small" />}
              </IconButton>
              <IconButton
                aria-label="Open navigation"
                onClick={() => setDrawerOpen(true)}
                sx={{ color: 'text.primary' }}
              >
                <MenuIcon />
              </IconButton>

              <Drawer
                anchor={isArabic ? 'right' : 'left'}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                  sx: {
                    width: 320,
                    maxWidth: '85vw',
                    bgcolor: 'background.default',
                    color: 'text.primary',
                    borderRight: '1px solid',
                    borderColor: 'divider'
                  }
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <Box
                      component="img"
                      src={brandIcon}
                      alt={t('brand.name')}
                      sx={{ height: 30, width: 'auto', display: 'block' }}
                    />
                    <Typography sx={{ fontFamily: 'var(--font-serif)', fontSize: '1.5rem' }}>
                      {t('brand.name')}
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      fontFamily: 'var(--font-mono)',
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.14em',
                      mt: 0.5
                    }}
                  >
                    {t('brand.tagline')}
                  </Typography>
                </Box>
                <Divider />

                <List dense disablePadding>
                  <ListItemButton component={RouterLink} to="/platform">
                    <ListItemText primary={t('nav.product')} />
                  </ListItemButton>

                  <ListItemButton component={RouterLink} to="/architecture">
                    <ListItemText primary={t('nav.capabilities')} />
                  </ListItemButton>

                  <ListItemButton onClick={() => setMobileSectorsOpen((v) => !v)}>
                    <ListItemText primary={t('nav.sectors')} />
                    {mobileSectorsOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={mobileSectorsOpen} timeout="auto" unmountOnExit>
                    <List dense disablePadding sx={{ pl: 1 }}>
                      {sectors.map((s) => (
                        <ListItemButton key={s.to} component={RouterLink} to={s.to} sx={{ pl: 3 }}>
                          <ListItemText primary={s.label} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>

                  <ListItemButton component={RouterLink} to="/blog">
                    <ListItemText primary={t('nav.research')} />
                  </ListItemButton>

                  <ListItemButton component={RouterLink} to="/media">
                    <ListItemText primary={t('nav.media')} />
                  </ListItemButton>

                  <ListItemButton component={RouterLink} to="/about">
                    <ListItemText primary={t('nav.company')} />
                  </ListItemButton>
                </List>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ px: 2, pb: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <LanguageIcon fontSize="small" />
                    <Typography fontWeight={600}>{t('common.language')}</Typography>
                  </Stack>
                  <Stack spacing={1}>
                    {['en', 'fr', 'ar'].map((lng) => (
                      <Button
                        key={lng}
                        variant={i18n.language === lng ? 'contained' : 'outlined'}
                        onClick={() => setLanguage(lng)}
                        sx={{ justifyContent: 'flex-start' }}
                        fullWidth
                      >
                        {t(`common.${lng === 'en' ? 'english' : lng === 'fr' ? 'french' : 'arabic'}`)}
                      </Button>
                    ))}
                  </Stack>

                  <Button
                    fullWidth
                    variant="contained"
                    component="a"
                    href={URBANAI_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ mt: 2 }}
                  >
                    {t('nav.tryPlayground')}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    component={RouterLink}
                    to="/contact"
                    sx={{ mt: 1 }}
                  >
                    {t('nav.talkToSales')}
                  </Button>
                </Box>
              </Drawer>
            </>
          ) : (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Button component={RouterLink} to="/platform" sx={linkSx}>
                {t('nav.product')}
              </Button>

              <Button component={RouterLink} to="/architecture" sx={linkSx}>
                {t('nav.capabilities')}
              </Button>

              <Button
                onClick={(e) => setSectorsAnchorEl(e.currentTarget)}
                sx={linkSx}
                endIcon={<ExpandMore fontSize="small" />}
                aria-haspopup="menu"
                aria-expanded={sectorsOpen ? 'true' : undefined}
              >
                {t('nav.sectors')}
              </Button>
              <Menu
                anchorEl={sectorsAnchorEl}
                open={sectorsOpen}
                onClose={() => setSectorsAnchorEl(null)}
                MenuListProps={{ 'aria-label': 'sectors menu' }}
                PaperProps={{ sx: { minWidth: 220, borderRadius: 2, mt: 0.5, border: '1px solid', borderColor: 'divider' } }}
              >
                {sectors.map((s) => (
                  <MenuItem
                    key={s.to}
                    component={RouterLink}
                    to={s.to}
                    onClick={() => setSectorsAnchorEl(null)}
                    sx={{ fontSize: '0.9rem' }}
                  >
                    {s.label}
                  </MenuItem>
                ))}
              </Menu>

              <Button component={RouterLink} to="/blog" sx={linkSx}>
                {t('nav.research')}
              </Button>

              <Button component={RouterLink} to="/media" sx={linkSx}>
                {t('nav.media')}
              </Button>

              <Button component={RouterLink} to="/about" sx={linkSx}>
                {t('nav.company')}
              </Button>

              <Box sx={{ width: 8 }} />

              <Tooltip title={mode === 'dark' ? t('common.lightMode') : t('common.darkMode')}>
                <IconButton
                  onClick={toggle}
                  size="small"
                  sx={{ color: 'text.primary' }}
                  aria-label={mode === 'dark' ? t('common.lightMode') : t('common.darkMode')}
                >
                  {mode === 'dark' ? <LightModeOutlined fontSize="small" /> : <DarkModeOutlined fontSize="small" />}
                </IconButton>
              </Tooltip>

              <Tooltip title={t('common.language')}>
                <IconButton
                  aria-label={t('common.language')}
                  onClick={(e) => setLangAnchorEl(e.currentTarget)}
                  size="small"
                  sx={{ color: 'text.primary' }}
                >
                  <LanguageIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={langAnchorEl}
                open={langOpen}
                onClose={() => setLangAnchorEl(null)}
                MenuListProps={{ 'aria-label': 'language menu' }}
              >
                <MenuItem onClick={() => setLanguage('en')}>{t('common.english')}</MenuItem>
                <MenuItem onClick={() => setLanguage('fr')}>{t('common.french')}</MenuItem>
                <MenuItem onClick={() => setLanguage('ar')}>{t('common.arabic')}</MenuItem>
              </Menu>

              <Button
                component={RouterLink}
                to="/contact"
                variant="text"
                sx={{ ...linkSx, ml: 1 }}
              >
                {t('nav.talkToSales')}
              </Button>
              <Button
                component="a"
                href={URBANAI_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                size="small"
                sx={{
                  ml: 0.5,
                  bgcolor: 'text.primary',
                  color: 'background.default',
                  '&:hover': { bgcolor: 'text.primary', opacity: 0.9 }
                }}
              >
                {t('nav.tryPlayground')}
              </Button>
            </Stack>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
}
